import fp from "lodash/fp"

import type { TFunction } from "i18next"

import type { DetailsGroup } from "types/common.types"
import type { Device, DeviceWGroups, IDeviceWRelsById } from "types/device.types"
import type { Powerline } from "types/powerlines.types"
import type { Tower } from "types/towers.types"

import { customValueFormatter } from "helpers/formatters/plots/plots"
import { formatLength } from "helpers/utils/powerlines"

export const getTowersById = (towers: Tower[]): { [key: number]: Tower } =>
  fp.indexBy((tower: Tower) => tower.id)(towers)

export const checkTowersByIdLoaded = (
  towerIds: number[],
  towersById: {
    [key: number]: Tower
  },
): boolean => fp.every((id: number) => fp.has(id, towersById))(towerIds)

export const getTowerDevicesFromRow = <T extends { device: Device }>(
  devices: T[],
  towerId: number,
): T[] => {
  return fp.filter(
    (row: T) =>
      row.device.settings?.installation_type === "tower" &&
      row.device.settings?.tower_id === towerId,
  )(devices)
}

export const getTowerDevicesIds = (
  devices: IDeviceWRelsById,
  towerId: number,
): number[] => {
  return fp.flow(
    fp.values,
    fp.filter(
      (d: DeviceWGroups) =>
        d.device.settings?.installation_type === "tower" &&
        d.device.settings?.tower_id === towerId,
    ),
    fp.map((d: DeviceWGroups) => d.device.id),
  )(devices)
}

export const getTowerPowerline = (
  towerId: number,
  powerlinesById: Record<number, Powerline>,
  powerlineIdByTowerId: Record<number, number>,
): Powerline | null => {
  const powerlineId = powerlineIdByTowerId[towerId]
  return powerlineId ? powerlinesById[powerlineId] || null : null
}

const formatAngle = (value: number, locale: string) =>
  customValueFormatter({ suffix: "°", decimalPlaces: 2, formatUnits: false, locale })(
    value,
  )

const formatCoordinate = (value: number, locale: string) =>
  customValueFormatter({ decimalPlaces: 6, formatUnits: false, locale })(value)

/** Most tower attributes are optional, and a missing one reads as a dash. */
const formatOptional = (
  value: number | null | undefined,
  locale: string,
  format: (value: number, locale: string) => string,
): string | undefined => (fp.isNil(value) ? undefined : format(value, locale))

/** The enums can grow before the translations do, so an unknown value shows as stored. */
const getEnumLabel = (prefix: string, value: string, t: TFunction): string =>
  t(`${prefix}.${value}`, { defaultValue: fp.capitalize(value.replaceAll("_", " ")) })

/** The tower details card, group by group. */
export const getTowerDetailsGroups = (
  tower: Tower,
  locale: string,
  t: TFunction,
): DetailsGroup[] => [
  {
    titleKey: "tower_information.GENERAL",
    fields: [
      { labelKey: "generic.NAME", value: tower.name ?? undefined },
      { labelKey: "generic.ID", value: tower.id },
      {
        labelKey: "tower_information.TYPE",
        value: t(`tower_information.TYPES.${tower.type.toUpperCase()}`, tower.type),
      },
      {
        labelKey: "tower_information.FUNCTIONALITY",
        value: tower.functionality
          ? getEnumLabel("tower_information.functionalities", tower.functionality, t)
          : undefined,
      },
      {
        labelKey: "tower_information.HEIGHT",
        value: formatOptional(tower.height, locale, formatLength),
      },
    ],
  },
  {
    titleKey: "tower_information.LOCATION",
    fields: [
      {
        labelKey: "generic.LATITUDE",
        value: formatOptional(tower.geo_location?.latitude, locale, formatCoordinate),
      },
      {
        labelKey: "generic.LONGITUDE",
        value: formatOptional(tower.geo_location?.longitude, locale, formatCoordinate),
      },
      {
        labelKey: "tower_information.ALTITUDE",
        value: formatOptional(tower.geo_location?.altitude, locale, formatLength),
      },
      {
        labelKey: "tower_information.ORIENTATION",
        value: formatOptional(tower.orientation, locale, formatAngle),
        tooltipContent: [t("tower_information.ORIENTATION_HINT")],
      },
      {
        labelKey: "tower_information.DEFLECTION_ANGLE",
        value: formatOptional(tower.deflection_angle, locale, formatAngle),
        tooltipContent: [t("tower_information.DEFLECTION_ANGLE_HINT")],
      },
    ],
  },
  {
    titleKey: "tower_information.INSULATORS",
    fields: [
      {
        labelKey: "tower_information.INSULATOR_TYPE",
        value: tower.insulator_type
          ? getEnumLabel("tower_information.insulatorTypes", tower.insulator_type, t)
          : undefined,
      },
      {
        labelKey: "tower_information.INSULATOR_LENGTH",
        value: formatOptional(tower.insulator_length, locale, formatLength),
      },
    ],
  },
]
